import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import WifiTetheringIcon from '@mui/icons-material/WifiTethering';
import PeopleIcon from '@mui/icons-material/People';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import SellIcon from '@mui/icons-material/Sell';
import ComputerIcon from '@mui/icons-material/Computer';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import HistoryIcon from '@mui/icons-material/History';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import ShieldIcon from '@mui/icons-material/Shield';
import { useAuth } from '../contexts/AuthContext';
import { useThemeMode } from '../contexts/ThemeModeContext';
import Footer from './Footer';

const SIDEBAR_WIDTH = 180;
const SIDEBAR_GAP = 16;
const APPBAR_HEIGHT = 64;

const MENU = [
  {
    group: '현황',
    items: [
      { Icon: DashboardIcon, path: '/admin', label: '대시보드', exact: true },
      { Icon: WifiTetheringIcon, path: '/admin/online', label: '현재 접속' },
    ],
  },
  {
    group: '회원',
    items: [
      { Icon: PeopleIcon, path: '/admin/users', label: '회원 관리' },
      { Icon: CardMembershipIcon, path: '/admin/subscriptions', label: '서버구독 관리' },
      { Icon: SellIcon, path: '/admin/plans', label: '요금제' },
    ],
  },
  {
    group: '접속',
    items: [
      { Icon: ComputerIcon, path: '/admin/agents', label: '에이전트' },
      { Icon: SystemUpdateAltIcon, path: '/admin/agent-releases', label: '에이전트 배포' },
      { Icon: HistoryIcon, path: '/admin/history', label: '접속·작업 이력' },
    ],
  },
];

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { isDark, setThemeMode } = useThemeMode();
  const [logoutOpen, setLogoutOpen] = useState(false);

  const isActive = (item) =>
    item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="sticky">
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Typography
            variant="h5"
            role="button"
            tabIndex={0}
            onClick={() => navigate('/admin')}
            sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', userSelect: 'none' }}
          >
            <ShieldIcon fontSize="medium" color="primary" />
            PlanGo Auth 관리자
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
              {user?.username} ({user?.email})
            </Typography>
            <Tooltip title={isDark ? '밝은 테마' : '어두운 테마'}>
              <IconButton onClick={() => setThemeMode(isDark ? 'white' : 'dark')}>
                {isDark ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title="내 계정">
              <IconButton onClick={() => navigate('/settings')}>
                <AccountCircleIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="로그아웃">
              <IconButton onClick={() => setLogoutOpen(true)}>
                <LogoutIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        <Box
          component="nav"
          sx={{
            position: 'sticky',
            top: APPBAR_HEIGHT + SIDEBAR_GAP,
            ml: `${SIDEBAR_GAP}px`,
            mt: `${SIDEBAR_GAP}px`,
            width: SIDEBAR_WIDTH,
            flexShrink: 0,
            maxHeight: `calc(100vh - ${APPBAR_HEIGHT + SIDEBAR_GAP * 2}px)`,
            overflowY: 'auto',
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1.5,
            boxShadow: isDark ? '0 2px 10px rgba(0,0,0,0.45)' : '0 2px 10px rgba(0,0,0,0.08)',
          }}
        >
          {MENU.map((section) => (
            <List
              key={section.group}
              dense
              disablePadding
              sx={{ '&:last-of-type > :last-child': { borderBottom: 0 } }}
              subheader={
                <Typography
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    color: isDark ? 'text.secondary' : '#0969da',
                    bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.045)',
                    borderBottom: 1,
                    borderColor: 'divider',
                  }}
                >
                  {section.group}
                </Typography>
              }
            >
              {section.items.map((item) => (
                <ListItemButton
                  key={item.path}
                  selected={isActive(item)}
                  onClick={() => navigate(item.path)}
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    borderBottom: 1,
                    borderColor: 'divider',
                    '&.Mui-selected, &.Mui-selected:hover': {
                      bgcolor: isDark ? 'rgba(144, 202, 249, 0.22)' : '#3a3a3a',
                      color: isDark ? 'text.primary' : '#fff',
                    },
                    '&.Mui-selected .MuiListItemIcon-root': {
                      color: isDark ? 'text.primary' : '#fff',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 30, color: 'text.secondary' }}>
                    <item.Icon sx={{ fontSize: '1.05rem' }} />
                  </ListItemIcon>
                  <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 400 }} />
                </ListItemButton>
              ))}
            </List>
          ))}
        </Box>

        <Box
          component="main"
          sx={{
            flex: 1,
            minWidth: 0,
            p: 3,
            pt: `${SIDEBAR_GAP}px`,
            minHeight: `calc(100vh - ${APPBAR_HEIGHT}px)`,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Outlet />
          </Box>
          <Footer />
        </Box>
      </Box>

      <Dialog open={logoutOpen} onClose={() => setLogoutOpen(false)}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LogoutIcon color="primary" />
          로그아웃 확인
        </DialogTitle>
        <DialogContent>
          <DialogContentText>정말 로그아웃 하시겠습니까?</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setLogoutOpen(false)}>취소</Button>
          <Button
            variant="contained"
            onClick={async () => {
              setLogoutOpen(false);
              await logout();
              navigate('/login');
            }}
          >
            로그아웃
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Layout;
